import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import useItemDialogFocus from "./useItemDialogFocus";
import { distributorSearchText, getDistributorTranslator } from "./distributorMessages";
import { supplierIdentityIds } from "../utils/supplierIdentity";
import "../PurchasingUi.css";
import "./SupplierDistributors.css";

const validId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const initialForm = (distributor) => ({
  name: distributor?.name || "", tax_id: distributor?.tax_id || "", email: distributor?.email || "",
  phone: distributor?.phone || "", address: distributor?.address || "", aliases: (distributor?.aliases || []).join("\n"),
});

/** A local form: the caller decides whether saving stages a draft or calls the API. */
export default function DistributorFormDialog({ isOpen, initialDistributor = null, suppliers = [], initialSupplierIds = [],
  showSupplierSelection = true, requireSupplier = true, onSave, onClose, saveLabel = "Save distributor", description, portalTarget }) {
  const { themeId, languageId } = usePmsTheme();
  const t = getDistributorTranslator(languageId);
  const id = useId();
  const [form, setForm] = useState(() => initialForm(initialDistributor));
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const initial = useRef();
  initial.current = { initialDistributor, initialSupplierIds };
  const close = () => { if (!busy.current) onClose(); };
  const dialogRef = useItemDialogFocus(isOpen, saving, close);
  useEffect(() => {
    if (!isOpen) return;
    const { initialDistributor: distributor, initialSupplierIds: supplierIds } = initial.current;
    setForm(initialForm(distributor));
    setSelected([...new Set((supplierIds.length ? supplierIds : (distributor?.suppliers || []).map((entry) => entry.id)).filter(validId).map(Number))]);
    setQuery(""); setError(""); setSaving(false); busy.current = false;
  }, [isOpen]);
  const visibleSuppliers = suppliers.filter((supplier, index) => validId(supplier.id) && suppliers.findIndex((entry) => Number(entry.id) === Number(supplier.id)) === index);
  const selectedFor = (supplier) => selected.some((supplierId) => supplierIdentityIds(supplier).includes(supplierId));
  const terms = distributorSearchText(query).split(/\s+/).filter(Boolean);
  const filtered = visibleSuppliers.filter((supplier) => terms.every((term) => distributorSearchText([supplier.name, supplier.supplier_id, supplier.tax_id].join(" ")).includes(term)));
  const change = (event) => setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault(); event.stopPropagation();
    if (busy.current) return;
    if (!form.name.trim()) { setError("Distributor name is required."); return; }
    const supplierIds = selected.filter((supplierId) => visibleSuppliers.some((supplier) => supplierIdentityIds(supplier).includes(supplierId)));
    if (showSupplierSelection && requireSupplier && !supplierIds.length) { setError("Select at least one supplier."); return; }
    busy.current = true; setSaving(true); setError("");
    const payload = Object.fromEntries(["name", "tax_id", "email", "phone", "address"].map((key) => [key, String(form[key]).trim()]));
    payload.aliases = [...new Set(form.aliases.split(/\r?\n/).map((alias) => alias.trim()).filter(Boolean))];
    if (showSupplierSelection) payload.supplier_ids = supplierIds;
    try { await onSave(payload); busy.current = false; setSaving(false); onClose(); }
    catch (requestError) {
      setError(requestError?.response?.data?.error || "The distributor could not be saved. Your changes are preserved.");
      busy.current = false; setSaving(false);
    }
  };
  const field = (name, label, { multiline = false, full = false, type = "text", required = false } = {}) => <label className={`dist-field${full ? " dist-field--full" : ""}`} htmlFor={`${id}-${name}`}>
    <span>{t(label)}{required ? " *" : ""}</span>
    {multiline ? <textarea id={`${id}-${name}`} name={name} value={form[name]} onChange={change} rows={2} disabled={saving} />
      : <input id={`${id}-${name}`} name={name} value={form[name]} onChange={change} type={type} required={required} disabled={saving} />}
    {name === "aliases" && <small>{t("One name per line.")}</small>}
  </label>;

  if (!isOpen) return null;
  return createPortal(<div className="pilcore-purchasing-overlay dist-overlay" data-theme={themeId} onMouseDown={(event) => {
    event.stopPropagation(); if (event.target === event.currentTarget) close();
  }}>
    <section className="dist-dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-description` : undefined} ref={dialogRef} tabIndex={-1}>
      <header><h2 id={`${id}-title`}>{t(initialDistributor?.id || initialDistributor?.__draftId ? "Edit distributor" : "Add distributor")}</h2>
        <button className="dist-button" type="button" disabled={saving} onClick={close}>{t("Close")}</button></header>
      <form onSubmit={submit} aria-busy={saving}>
        <div className="dist-dialog-body">
          {description && <p id={`${id}-description`}>{t(description)}</p>}
          <div className="dist-fields">
            {field("name", "Distributor name", { required: true })}{field("tax_id", "Tax ID")}
            {field("email", "Email", { type: "email" })}{field("phone", "Phone", { type: "tel" })}
            {field("address", "Address", { multiline: true, full: true })}{field("aliases", "Other names on delivery notes", { multiline: true, full: true })}
          </div>
          {showSupplierSelection && <fieldset className="dist-suppliers" disabled={saving}>
            <legend>{t("Suppliers served")}</legend><p>{t("Select one or more suppliers.")}</p>
            <label className="dist-field" htmlFor={`${id}-search`}><span>{t("Search suppliers")}</span>
              <input id={`${id}-search`} type="search" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
            <p role="status">{t("{count} suppliers selected", { count: visibleSuppliers.filter(selectedFor).length })}</p>
            <div className="dist-option-grid">{filtered.map((supplier) => <label className="dist-option" key={supplier.id}>
              <input type="checkbox" checked={selectedFor(supplier)} onChange={() => setSelected((previous) => previous.some((supplierId) => supplierIdentityIds(supplier).includes(supplierId))
                ? previous.filter((supplierId) => !supplierIdentityIds(supplier).includes(supplierId)) : [...previous, Number(supplier.id)])} />
              <span><strong>{supplier.name}</strong><small>{[supplier.supplier_id, supplier.tax_id].filter(Boolean).join(" · ")}</small></span>
            </label>)}</div>
            {!filtered.length && <p>{t(visibleSuppliers.length ? "No suppliers match your search." : "No suppliers available.")}</p>}
          </fieldset>}
          {error && <p className="dist-error" role="alert">{t(error)}</p>}
        </div>
        <footer><button className="dist-button" type="button" onClick={close} disabled={saving}>{t("Cancel")}</button>
          <button className="dist-button dist-button--primary" type="submit" disabled={saving}>{t(saving ? "Saving…" : saveLabel)}</button></footer>
      </form>
    </section>
  </div>, portalTarget || document.body);
}
